import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getDevice, getDevices } from './routes/devices';
import { getDevicePlaces, updateBalance } from './routes/places';
import type {
  GetDevicePlacesResponse,
  UpdateBalanceRequest,
  UpdateBalanceResponse,
} from './routes/places';

export const queryKeys = {
  devices: ['devices'] as const,
  device: (deviceId: number) => ['device', deviceId] as const,
  devicePlaces: (deviceId: number) => ['devicePlaces', deviceId] as const,
};

export const useDevices = () => {
  return useQuery({
    queryKey: queryKeys.devices,
    queryFn: getDevices,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDevice = (deviceId: number | null) => {
  return useQuery({
    queryKey: queryKeys.device(deviceId!),
    queryFn: () => getDevice({ deviceId: deviceId! }),
    enabled: deviceId !== null,
    staleTime: 2 * 60 * 1000,
  });
};

export const useDevicePlaces = (deviceId: number | null) => {
  return useQuery<GetDevicePlacesResponse, Error>({
    queryKey: queryKeys.devicePlaces(deviceId!),
    queryFn: () => getDevicePlaces({ deviceId: deviceId! }),
    enabled: deviceId !== null,
    staleTime: 2 * 60 * 1000,
  });
};

export const useUpdatePlaceBalance = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateBalanceResponse, Error, UpdateBalanceRequest>({
    mutationFn: updateBalance,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.device(variables.deviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.devicePlaces(variables.deviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.devices });
    },
  });
};
